<template>
  <div class="content-scrollable min-h-0">
    <GeofenceForm
      v-if="isFormReady"
      :key="suggestionId ?? 'blank'"
      @submit="submit"
      :is-b2b="isB2B"
      :initial-values="initialValues"
    >
      <div class="w-full">
        <Button size="sm" variant="destructive" class="w-full" @click="cancel">
          Cancel
        </Button>
      </div>
      <div
        class="w-full"
        @mouseenter="setSaveButtonHoverState(true)"
        @mouseleave="setSaveButtonHoverState(false)"
      >
        <UITooltip
          class="w-full"
          text="Must draw geofence on the map to save"
          :disabled="Boolean(shape) || !shouldShowSaveHelp"
        >
          <Button
            type="submit"
            size="sm"
            class="w-full"
            :class="[{ 'cursor-help': shouldShowSaveHelp }]"
            :disabled="!shape"
            :is-loading="isSaving"
            is-loading-text="Saving..."
          >
            Save
          </Button>
        </UITooltip>
      </div>
    </GeofenceForm>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useQueryClient } from '@tanstack/vue-query';
import { getAccountPlaceSuggestion } from '@gpx-spa/queries';
import {
  geometryCenter,
  normalizePolygon,
} from '@/components/place-suggestions/geometry';
import GeofenceForm from '@/components/geofence/_common/GeofenceForm.vue';
import { geofenceCreationDefaults } from '@/utils/account-defaults';
import CommonSearchBarSafeArea from '@/components/_common/CommonSearchBarSafeArea.vue';
import { COMMON_SAVE_ERROR, COMMON_SAVE_SUCCESS } from '@/config/constants';
import { useSidebarIsOpen } from '@/composables/useSidebarIsOpen';
import { RIGHT_SIDEBAR } from '@/types/ui';
import * as events from '@/events';

const toast = useToast();
const store = useStore();
const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const isRightSidebarOpen = useSidebarIsOpen(RIGHT_SIDEBAR, false);

const isSaving = ref(false);
const isSaveButtonHovered = ref(false);

const isB2B = store.getters['auth/isB2B'];
const accountId = store.getters['auth/accountId'];
const shape = computed(() => store.getters['geofence/shape']);
const shouldShowSaveHelp = computed(() => {
  return !shape.value && isSaveButtonHovered.value;
});

onMounted(() => {
  if (route.query.suggestion_id) {
    isRightSidebarOpen.value = false;
  }
});

onUnmounted(() => {
  store.dispatch('geofence/setViewportObj', null);
  store.dispatch('geofence/setShape', null);
});

const suggestionId = computed(() => route.query.suggestion_id ?? null);
const accountIdRef = computed(() => accountId ?? null);

const { suggestion, isLoading: isSuggestionLoading } =
  getAccountPlaceSuggestion(accountIdRef, suggestionId);

const isFormReady = computed(() => {
  if (!suggestionId.value) return true;
  return !isSuggestionLoading.value && !!suggestion.value;
});

/** Account-wide starting values an admin set under Settings (ENG-2747). */
const creationDefaults = () =>
  geofenceCreationDefaults(store.getters['auth/accountSettings']);

const initialValues = ref({
  address: { label: '', lat: null, lng: null },
  place_type: null,
  ownership_type: null,
  ...creationDefaults(),
});

watch(
  suggestion,
  (s) => {
    if (!s) return;
    const geom = normalizePolygon(s.polygon);
    if (!geom) return;

    const center = geometryCenter(geom);
    if (!center) return;
    const [centerLat, centerLng] = center;

    initialValues.value = {
      name: s.name?.trim() ?? '',
      address: {
        label: s.address ?? '',
        lat: centerLat,
        lng: centerLng,
      },
      place_type: null,
      ownership_type: null,
      ...creationDefaults(),
    };

    store.dispatch('geofence/setShape', {
      is_irregular: 1,
      geo_json: JSON.stringify(geom),
    });
  },
  { immediate: true },
);

function submit(params) {
  isSaving.value = true;
  const suggestionId = route.query.suggestion_id;
  const enriched = suggestionId
    ? { ...params, suggestion_id: suggestionId }
    : params;
  store
    .dispatch('geofence/store', { accountId, params: enriched })
    .then((res) => {
      if (suggestionId) {
        queryClient.invalidateQueries({
          queryKey: ['place-suggestions', accountId],
        });
        queryClient.invalidateQueries({
          queryKey: ['place-suggestion', accountId, suggestionId],
        });
      }
      router.replace({
        name: 'GeofenceDetailsView',
        params: { id: res.data.data.id },
      });
      events.trigger(events.names.GEOFENCE_CREATED, {
        id: res.data.data.id,
        ...params,
      });
      toast.success(COMMON_SAVE_SUCCESS);
    })
    .catch(() => {
      isSaving.value = false;
      toast.error(COMMON_SAVE_ERROR);
    });
}

function cancel() {
  router.push({
    name: 'GeofenceListView',
    query: { search: store.getters['geofence/search'] },
  });
}

function setSaveButtonHoverState(value) {
  if (shape.value) return;
  isSaveButtonHovered.value = value;
}
</script>
